// Hi-fi product UI previews — drop-in "screenshots" of the Learnforest app,
// drawn with HTML/CSS at brand fidelity to replace stock illustrations.
//
// The two scroll-triggered animations (ProgressPreview chart-draw and
// AccountsPreview bubble-drop) are driven by lf_fe/src/cards/indexV1/
// animations.js — see that file for the IntersectionObserver + rAF loop.
// The same module is inlined into the static snapshot at
// lf_fe/public/www/index.html, so animations work in both routes.

export function BrowserChrome({ children, className = "" }) {
  return (
    <div className={`rounded-2xl overflow-hidden bg-white iv1-soft-shadow-lg border border-black/5 ${className}`}>
      <div className="h-8 bg-[#f3f1ec] flex items-center gap-1.5 px-3 border-b border-black/5">
        <span className="w-2.5 h-2.5 rounded-full bg-[#ff6058]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#28c940]" />
      </div>
      <div className="bg-white">{children}</div>
    </div>
  );
}

export function VideoPreview() {
  return (
    <BrowserChrome className="w-full">
      <div className="p-4 bg-[#fafaf7]">
        <div className="relative rounded-xl overflow-hidden aspect-video bg-gradient-to-br from-[#1d2a4a] to-[#0f1530] iv1-soft-shadow">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <h3 className="iv1-font-display text-white/95 text-3xl font-bold leading-tight tracking-tight">
                The shape of
                <br />a parabola
              </h3>
              <div className="mt-2 font-mono text-[10px] text-white/50">LESSON 2 · QUADRATICS</div>
            </div>
          </div>
          <div className="absolute top-3 left-3 px-2 py-1 rounded-md bg-white/10 backdrop-blur font-mono text-[10px] text-white/90">
            y = ax² + bx + c
          </div>
          <div className="absolute top-3 right-3 px-2 py-1 rounded-md bg-black/40 backdrop-blur text-[10px] text-white/90 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
            Curated · Ad-free
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-xl">
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path fill="#282846" d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
          <div className="absolute bottom-0 inset-x-0 p-3">
            <div className="h-1 rounded-full bg-white/20">
              <div className="h-1 rounded-full bg-white w-1/3 relative">
                <div className="absolute -right-1.5 -top-1 w-3 h-3 rounded-full bg-white shadow" />
              </div>
            </div>
            <div className="flex justify-between text-white/80 text-[10px] mt-1.5 font-mono">
              <span>2:14</span>
              <span>6:48</span>
            </div>
          </div>
        </div>
        <div className="mt-3 flex gap-2 flex-wrap">
          <span className="px-2.5 py-1 rounded-full bg-pastelblue-light text-darkrock text-[10px] font-semibold">📝 AI summary</span>
          <span className="px-2.5 py-1 rounded-full bg-pastelyellow-light text-darkrock text-[10px] font-semibold">💡 3 key ideas</span>
          <span className="px-2.5 py-1 rounded-full bg-pastelgreen-light text-darkrock text-[10px] font-semibold">❓ Quiz ready</span>
        </div>
      </div>
    </BrowserChrome>
  );
}

export function StudyPlanPreview() {
  const lessons = [
    { t: "Introduction to parabolas", d: "8 min", done: true },
    { t: "Standard form & coefficients", d: "11 min", done: true },
    { t: "Factoring quadratics", d: "14 min", active: true },
    { t: "The discriminant", d: "9 min" },
    { t: "Real-world applications", d: "12 min" },
  ];
  return (
    <BrowserChrome className="w-full">
      <div className="p-5 bg-white">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] text-darkrock/50 tracking-wider">STUDY PLAN</div>
            <h3 className="iv1-font-display font-bold text-darkrock text-xl mt-0.5">Quadratic equations</h3>
            <div className="text-[11px] text-darkrock/60 mt-0.5">Goal: master Algebra II by June</div>
          </div>
          <div className="w-12 h-12 rounded-full bg-pastelgreen flex items-center justify-center text-lg">📐</div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <div className="flex-1 h-2 rounded-full bg-pastelblue-light overflow-hidden">
            <div className="h-full bg-primary-600 rounded-full" style={{ width: "40%" }} />
          </div>
          <span className="font-mono text-[10px] text-darkrock/70">2 / 5</span>
        </div>
        <div className="mt-4 space-y-1.5">
          {lessons.map((l, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg ${
                l.active ? "bg-pastelblue-light ring-1 ring-primary-600/30" : "bg-[#fafaf7]"
              }`}
            >
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                  l.done
                    ? "bg-emerald-500 text-white"
                    : l.active
                    ? "bg-primary-600 text-white"
                    : "bg-white border border-black/10 text-darkrock/40"
                }`}
              >
                {l.done ? "✓" : i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-[12px] font-semibold truncate ${l.done ? "text-darkrock/50 line-through" : "text-darkrock"}`}>
                  {l.t}
                </div>
                <div className="text-[10px] text-darkrock/50">{l.d}</div>
              </div>
              {l.active && <span className="px-2 py-0.5 rounded-full bg-primary-600 text-white text-[9px] font-bold">NEXT</span>}
            </div>
          ))}
        </div>
      </div>
    </BrowserChrome>
  );
}

export function QuizPreview() {
  const options = [
    { t: "x = 1", s: "default" },
    { t: "x = 2", s: "correct" },
    { t: "x = 4", s: "default" },
    { t: "x = 5", s: "default" },
  ];
  return (
    <BrowserChrome className="w-full">
      <div className="p-5 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="font-mono text-[10px] text-darkrock/50">QUESTION 3 / 5</div>
          </div>
          <div className="flex gap-1">
            {[1, 1, 1, 0, 0].map((v, i) => (
              <div key={i} className={`w-5 h-1.5 rounded-full ${v ? "bg-pastelgreen" : "bg-black/10"}`} />
            ))}
          </div>
        </div>
        <h3 className="mt-4 iv1-font-display text-lg font-semibold text-darkrock leading-snug">
          Which value of <span className="font-mono bg-pastelyellow-light px-1.5 rounded">x</span> satisfies the equation?
        </h3>
        <div className="mt-2 font-mono text-darkrock/80 text-sm bg-[#fafaf7] rounded-lg px-3 py-2 inline-block">
          x² − 5x + 6 = 0
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          {options.map((opt, i) => (
            <div
              key={i}
              className={`px-3 py-2.5 rounded-xl border-2 flex items-center justify-between ${
                opt.s === "correct" ? "bg-pastelgreen-light border-emerald-500" : "bg-white border-black/10"
              }`}
            >
              <span className={`font-mono text-sm ${opt.s === "correct" ? "text-emerald-700 font-bold" : "text-darkrock"}`}>{opt.t}</span>
              {opt.s === "correct" && (
                <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center text-white text-[10px]">✓</div>
              )}
            </div>
          ))}
        </div>
        <div className="mt-3 px-3 py-2 rounded-lg bg-pastelgreen-light">
          <div className="text-[11px] text-emerald-800 font-semibold">
            Nice! Both x=2 and x=3 work — the quadratic factors as (x−2)(x−3).
          </div>
        </div>
      </div>
    </BrowserChrome>
  );
}

const PROGRESS_DRAW_DURATION_MS = 1200;
const PROGRESS_CLIP_ID = "iv1-completion-clip";

export function ProgressPreview() {
  const stats = [
    { l: "Lessons", v: "14", s: "+3" },
    { l: "Minutes", v: "127", s: "+22" },
    { l: "Quiz acc.", v: "86%", s: "+4%" },
  ];
  const trend = [12, 18, 24, 31, 38, 45, 52, 60, 68, 76, 84, 90];

  const width = 320;
  const height = 76;
  const padding = { top: 4, right: 28, bottom: 4, left: 4 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const points = trend.map((v, i) => ({
    x: padding.left + (i / (trend.length - 1)) * innerW,
    y: padding.top + innerH - (v / 100) * innerH,
    v,
  }));
  const polylinePoints = points.map((p) => `${p.x},${p.y}`).join(" ");
  const lastPoint = points[points.length - 1];

  return (
    <BrowserChrome className="w-full">
      <div className="p-5 bg-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] text-darkrock/50 tracking-wider">WEEKLY SUMMARY</div>
            <h3 className="iv1-font-display font-bold text-darkrock text-lg mt-0.5">Mia is on a 12-day streak 🔥</h3>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-8 h-8 rounded-full bg-pastelpurple flex items-center justify-center text-sm">🦊</div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          {stats.map((s, i) => (
            <div key={i} className="rounded-xl bg-[#fafaf7] px-3 py-2">
              <div className="text-[10px] text-darkrock/50 font-mono">{s.l}</div>
              <div className="iv1-font-display font-bold text-darkrock text-xl leading-none mt-1">{s.v}</div>
              <div className="text-[10px] text-emerald-600 font-semibold mt-0.5">{s.s}</div>
            </div>
          ))}
        </div>

        <div
          className="mt-4"
          data-iv1-anim="progress-chart"
          data-iv1-chart-width={innerW}
        >
          <div className="text-[10px] text-darkrock/50 font-mono mb-1.5">COMPLETION OVER TIME</div>
          <svg
            viewBox={`0 0 ${width} ${height}`}
            className="w-full h-auto"
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <clipPath id={PROGRESS_CLIP_ID}>
                <rect
                  data-iv1-anim="progress-chart-clip"
                  x={padding.left}
                  y={0}
                  width={0}
                  height={height}
                  style={{ transition: `width ${PROGRESS_DRAW_DURATION_MS}ms ease-out` }}
                />
              </clipPath>
            </defs>

            {[0, 25, 50, 75, 100].map((val) => {
              const y = padding.top + innerH - (val / 100) * innerH;
              return (
                <line
                  key={val}
                  x1={padding.left}
                  y1={y}
                  x2={padding.left + innerW}
                  y2={y}
                  stroke="#e5e7eb"
                  strokeWidth="0.5"
                />
              );
            })}

            <g clipPath={`url(#${PROGRESS_CLIP_ID})`}>
              <polygon
                points={`${points[0].x},${padding.top + innerH} ${polylinePoints} ${lastPoint.x},${padding.top + innerH}`}
                fill="#2563eb"
                fillOpacity="0.1"
              />
              <polyline
                points={polylinePoints}
                fill="none"
                stroke="#2563eb"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {points.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r="2" fill="#2563eb" />
              ))}
            </g>

            <text
              data-iv1-anim="progress-chart-label"
              x={lastPoint.x + 5}
              y={lastPoint.y + 3}
              fontSize="9"
              fontWeight="700"
              fill="#2563eb"
              style={{
                opacity: 0,
                transition: `opacity 300ms ease-out ${PROGRESS_DRAW_DURATION_MS - 150}ms`,
              }}
            >
              {lastPoint.v}%
            </text>
          </svg>
        </div>

        <div className="mt-3 px-3 py-2 rounded-lg bg-pastelblue-light">
          <div className="text-[10px] text-primary-800 font-mono font-bold mb-0.5">AI SUMMARY · TODAY</div>
          <div className="text-[11px] text-darkrock leading-snug">
            Mia practiced factoring quadratics and aced the quiz. Ask her how she'd solve x²−5x+6=0.
          </div>
        </div>
      </div>
    </BrowserChrome>
  );
}

export function ParentPlanPreview() {
  const subjects = [
    { e: "📐", l: "Math", active: true },
    { e: "🌤️", l: "Science" },
    { e: "🖋️", l: "English" },
    { e: "🏛️", l: "History" },
    { e: "♟️", l: "Chess" },
  ];
  return (
    <BrowserChrome className="w-full">
      <div className="p-5 bg-white">
        <div className="font-mono text-[10px] text-darkrock/50 tracking-wider">NEW STUDY PLAN · MIA, AGE 11</div>
        <h3 className="iv1-font-display font-bold text-darkrock text-lg mt-0.5">What should Mia learn next?</h3>

        <div className="mt-3">
          <div className="text-[10px] text-darkrock/60 font-semibold mb-1.5">SUBJECT</div>
          <div className="flex flex-wrap gap-1.5">
            {subjects.map((s, i) => (
              <div
                key={i}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold flex items-center gap-1 ${
                  s.active ? "bg-pastelgreen text-darkrock ring-1 ring-emerald-600/40" : "bg-[#fafaf7] text-darkrock/70"
                }`}
              >
                {s.e} {s.l}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-3">
          <div className="text-[10px] text-darkrock/60 font-semibold mb-1.5">GOAL</div>
          <div className="rounded-xl border border-black/10 bg-[#fafaf7] px-3 py-2 text-[12px] text-darkrock">
            Master quadratic equations by the end of May
            <span className="inline-block w-0.5 h-3 bg-primary-600 ml-0.5 align-middle animate-pulse" />
          </div>
        </div>

        <div className="mt-3 rounded-xl bg-pastelpurple-light p-3">
          <div className="text-[10px] text-darkrock/60 font-mono font-bold mb-1">✨ AI SUGGESTION</div>
          <div className="text-[12px] text-darkrock leading-snug">
            A 5-lesson plan covering factoring, the quadratic formula, and word problems — about 55 min total.
          </div>
          <button className="mt-2 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-[11px] font-semibold">
            Generate plan →
          </button>
        </div>
      </div>
    </BrowserChrome>
  );
}

export function ContentBlockingPreview() {
  const keywords = ["tiktok", "funny", "wait for it", "game", "prank"];
  return (
    <BrowserChrome className="w-full">
      <div className="p-5 bg-white">
        <div className="font-mono text-[10px] text-darkrock/50 tracking-wider">CONTENT FILTERS · MIA</div>
        <div className="iv1-font-display font-bold text-darkrock text-lg mt-0.5">Block by keyword</div>

        <div className="text-[10px] text-darkrock/60 font-semibold mt-4 mb-1.5">BLOCKED KEYWORDS</div>
        <div className="flex flex-wrap gap-1.5">
          {keywords.map((kw) => (
            <span
              key={kw}
              className="px-2.5 py-0.5 rounded-full bg-pastelyellow text-darkrock text-[10px] font-semibold flex items-center gap-1"
            >
              {kw}
              <span className="text-[9px] text-darkrock/40">×</span>
            </span>
          ))}
        </div>
        <div className="text-[11px] text-darkrock/30 mt-1.5 px-1">+ Add keyword…</div>

        <div className="mt-3 px-3 py-2 rounded-lg bg-pastelgreen-light text-[11px] text-darkrock leading-snug">
          ✓ Videos matching these keywords won't be recommended or shown in search
        </div>
      </div>
    </BrowserChrome>
  );
}

export function AccountsPreview() {
  const kids = [
    { e: "🦊", n: "Mia", c: "bg-pastelyellow" },
    { e: "🐢", n: "Leo", c: "bg-pastelgreen" },
    { e: "🐼", n: "Ava", c: "bg-pastelpurple" },
  ];

  return (
    <div
      data-iv1-anim="accounts-preview"
      className="w-full rounded-2xl bg-sky p-6 iv1-soft-shadow-lg relative overflow-hidden"
    >
      <div className="flex items-end justify-center gap-4 pt-6">
        {kids.map((k, i) => (
          <div key={i} className="text-center">
            <div
              data-iv1-anim="account-bubble"
              className={`w-16 h-16 rounded-full ${k.c} border-4 border-white shadow-lg flex items-center justify-center text-3xl mx-auto iv1-bubble-physics-enter`}
              style={{ opacity: 0 }}
            >
              {k.e}
            </div>
            <div className="mt-1.5 font-semibold text-darkrock text-sm">{k.n}</div>
          </div>
        ))}
        <div className="text-center">
          <div
            data-iv1-anim="account-bubble"
            className="w-16 h-16 rounded-full bg-white/80 border-4 border-white shadow-lg flex items-center justify-center text-2xl text-darkrock/50 mx-auto iv1-bubble-physics-enter"
            style={{ opacity: 0 }}
          >
            +
          </div>
          <div className="mt-1.5 font-semibold text-darkrock/60 text-sm">Add</div>
        </div>
      </div>
    </div>
  );
}
